import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useRouterState, type AnyRouteMatch } from '@tanstack/react-router';
import { useEffect, useReducer, type ReactNode } from 'react';

/**
 * Re-render on any React Query cache event so a `headerActions` resolver whose
 * output depends on cached data re-resolves once that data lands — without this
 * the actions only update on navigation. Mirrors `route-breadcrumbs.tsx`; the
 * `queueMicrotask` defer avoids React's "update a component while rendering a
 * different component" warning when a sibling mounts a query observer mid-render.
 */
function useRerenderOnQueryCache(queryClient: QueryClient): void {
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      queueMicrotask(forceRender);
    });
    return () => {
      unsubscribe();
    };
  }, [queryClient]);
}

/**
 * Resolve the most-specific route's `headerActions`, leaf-first — so a parent
 * layout route (e.g. `/quality-packs`) can supply one action for all of its
 * children, and a leaf can override it.
 */
function resolveHeaderActions(
  matches: readonly AnyRouteMatch[],
  queryClient: QueryClient,
): ReactNode {
  for (let i = matches.length - 1; i >= 0; i--) {
    const match = matches[i];
    if (!match) continue;
    const resolver = match.staticData.headerActions;
    if (typeof resolver === 'function') return resolver(match, queryClient);
  }
  return null;
}

/**
 * Renders the right-aligned page actions declared by the current route via
 * `staticData.headerActions` (see `router-types.d.ts`). Rendered inside the
 * `PageHeader`'s `actions` slot in `__root.tsx`; returns nothing when no route
 * in the chain declares actions, so the slot collapses.
 */
export function RouteHeaderActions() {
  const queryClient = useQueryClient();
  useRerenderOnQueryCache(queryClient);
  const matches = useRouterState({ select: (state) => state.matches });
  return resolveHeaderActions(matches, queryClient);
}
